const PENDING_TEXT = "待完善";

const LEGACY_TO_CANONICAL = {
  additionalLifeCoverageAmountOption:
    "insurancePurchaseObjectives_A_ProposedInsured",
  additionalLifeCoverageAmountHKD:
    "insurancePurchaseObjectives_A_ProposedInsured_A_amount",
  additionalLifeCoverageAmountUSD:
    "insurancePurchaseObjectives_A_ProposedInsured_B_amount",
  insurancePurchaseMedicalObjectives: "insurancePurchaseObjectives_B",
  additionalCriticalIllnessCoverageAmountOption:
    "insurancePurchaseObjectives_B_C_ProposedInsured",
  additionalCriticalIllnessCoverageAmountHKD:
    "insurancePurchaseObjectives_B_C_ProposedInsured_A_amount",
  additionalCriticalIllnessCoverageAmountUSD:
    "insurancePurchaseObjectives_B_C_ProposedInsured_B_amount",
  additionalTargetAmountOption: "insurancePurchaseObjectives_CDE_ProposedInsured",
  additionalTargetAmountHKD:
    "insurancePurchaseObjectives_CDE_ProposedInsured_A_amount",
  additionalTargetAmountUSD:
    "insurancePurchaseObjectives_CDE_ProposedInsured_B_amount",
  additionalTargetYears: "insurancePurchaseObjectives_CDE_year",
  insurancePurchaseOtherObjectiveOptions: "insurancePurchaseObjectives_F",
  insurancePurchaseOtherObjectives: "insurancePurchaseObjectives_F_E_remark",
  singlePremiumPaymentLimit: "willingPremiumPaymentPeriodRange_G_amount",
};

const CANONICAL_TO_LEGACY = Object.fromEntries(
  Object.entries(LEGACY_TO_CANONICAL).map(([legacy, canonical]) => [
    canonical,
    legacy,
  ]),
);

const LEGACY_CHECKBOX_TO_CANONICAL_RADIO = new Set([
  "insurancePurchaseMedicalObjectives",
  "insurancePurchaseOtherObjectiveOptions",
]);

const PAYMENT_PERIOD_MAPPING = {
  A: "2-5年",
  B: "6-10年",
  C: "11-15年",
  D: "16-20年",
  E: "超過20年",
  F: "終身",
  G: "一次性付款不超過",
};

const COVERAGE_PERIOD_MAPPING = {
  A: "少於1年",
  B: "1-5年",
  C: "6-10年",
  D: "11-15年",
  E: "16-20年",
  F: "超過20年",
  G: "終身",
};

const ZUU_OTHER_OBJECTIVE_LABELS = {
  A: "付款人保障",
  B: "付款人保障連危疾",
  C: "免付保費",
  D: "遺產規劃",
  E: "其他",
};

function toDisplayString(value) {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return "";
}

function getSelectedItemKeys(question) {
  return (question?.items || [])
    .filter((item) => item?.value === true)
    .map((item) => item?.key)
    .filter((key) => typeof key === "string" && key.trim() !== "");
}

function hasMeaningfulQuestionValue(question) {
  if (!question) {
    return false;
  }

  if (question.type === "INPUT") {
    return (question.items || []).some((item) => {
      const value = item?.value;
      return value !== undefined && value !== null && value !== "";
    });
  }

  return getSelectedItemKeys(question).length > 0;
}

function getZuuQuestionAliases(questionKey) {
  const canonicalKey = LEGACY_TO_CANONICAL[questionKey] || questionKey;
  const legacyKey = CANONICAL_TO_LEGACY[canonicalKey];
  return Array.from(
    new Set([questionKey, canonicalKey, legacyKey].filter(Boolean)),
  );
}

function getQuestionValueFromListByVariant(
  list,
  questionKey,
  type = "RADIO",
  options,
) {
  if (!options?.isZuuMode) {
    const question = list?.find((item) => item?.question === questionKey);
    if (!question) {
      return undefined;
    }

    if (type === "INPUT") {
      return question.items?.find((item) => item?.key === "A")?.value;
    }

    const selectedKeys = getSelectedItemKeys(question);
    return type === "CHECKBOX" ? selectedKeys : selectedKeys[0];
  }

  const aliases = getZuuQuestionAliases(questionKey);
  const question =
    aliases
      .map((key) => list?.find((item) => item?.question === key))
      .find((item) => hasMeaningfulQuestionValue(item)) ||
    aliases
      .map((key) => list?.find((item) => item?.question === key))
      .find(Boolean);

  if (!question) {
    return undefined;
  }

  if (type === "INPUT") {
    return question.items?.find((item) => item?.key === "A")?.value;
  }

  const selectedKeys = getSelectedItemKeys(question);
  if (type === "RADIO") {
    return selectedKeys[0];
  }

  if (LEGACY_CHECKBOX_TO_CANONICAL_RADIO.has(questionKey)) {
    return selectedKeys[0] ? [selectedKeys[0]] : [];
  }

  return selectedKeys;
}

function getInputValue(financialAnalysis, questionKey, isZuuMode) {
  return toDisplayString(
    getQuestionValueFromListByVariant(
      financialAnalysis,
      questionKey,
      "INPUT",
      { isZuuMode },
    ),
  );
}

function getSelectedKey(financialAnalysis, questionKey, isZuuMode) {
  return toDisplayString(
    getQuestionValueFromListByVariant(
      financialAnalysis,
      questionKey,
      "RADIO",
      { isZuuMode },
    ),
  );
}

function getSelectedKeys(financialAnalysis, questionKey, isZuuMode) {
  const value = getQuestionValueFromListByVariant(
    financialAnalysis,
    questionKey,
    "CHECKBOX",
    { isZuuMode },
  );

  return Array.isArray(value)
    ? value.filter((item) => typeof item === "string" && item.trim() !== "")
    : [];
}

function formatCurrencyAmount(amount, currency) {
  return amount ? `${amount}${currency}` : "";
}

function resolveLegacyObjectiveValue(financialAnalysis, objectiveKey) {
  switch (objectiveKey) {
    case "A":
      return formatCurrencyAmount(
        getInputValue(financialAnalysis, "additionalLifeCoverageAmount", false),
        "HKD",
      );
    case "B":
      return formatCurrencyAmount(
        getInputValue(
          financialAnalysis,
          "additionalCriticalIllnessCoverageAmount",
          false,
        ),
        "HKD",
      );
    case "C":
    case "D": {
      const period = getInputValue(
        financialAnalysis,
        "additionalSavingsTargetReturnPeriod",
        false,
      );
      const amount = getInputValue(
        financialAnalysis,
        "additionalSavingsTargetReturnAmount",
        false,
      );
      return period && amount ? `${period} - ${amount}HKD` : "";
    }
    case "E": {
      const period = getInputValue(
        financialAnalysis,
        "additionalInvestmentTargetReturnPeriod",
        false,
      );
      const amount = getInputValue(
        financialAnalysis,
        "additionalInvestmentTargetReturnAmount",
        false,
      );
      return period && amount ? `${period} - ${amount}HKD` : "";
    }
    case "F":
      return getInputValue(
        financialAnalysis,
        "insurancePurchaseOtherObjectives",
        false,
      );
    default:
      return "";
  }
}

function resolveZuuCurrencyObjectiveValue(
  financialAnalysis,
  optionQuestionKey,
  hkdQuestionKey,
  usdQuestionKey,
) {
  const option = getSelectedKey(financialAnalysis, optionQuestionKey, true);

  if (option === "A") {
    return formatCurrencyAmount(
      getInputValue(financialAnalysis, hkdQuestionKey, true),
      "HKD",
    );
  }

  if (option === "B") {
    return formatCurrencyAmount(
      getInputValue(financialAnalysis, usdQuestionKey, true),
      "USD",
    );
  }

  if (option === "C") {
    return "不適用";
  }

  return "";
}

function resolveZuuTargetAmountValue(financialAnalysis) {
  const years = getInputValue(financialAnalysis, "additionalTargetYears", true);
  const option = getSelectedKey(financialAnalysis, "additionalTargetAmountOption", true);

  if (!years) {
    return "";
  }

  if (option === "A") {
    const amount = getInputValue(financialAnalysis, "additionalTargetAmountHKD", true);
    return amount ? `${years} - ${amount}HKD` : years;
  }

  if (option === "B") {
    const amount = getInputValue(financialAnalysis, "additionalTargetAmountUSD", true);
    return amount ? `${years} - ${amount}USD` : years;
  }

  return years;
}

function resolveZuuOtherObjectiveValue(financialAnalysis) {
  const selectedKey = getSelectedKey(
    financialAnalysis,
    "insurancePurchaseObjectives_F",
    true,
  );

  if (!selectedKey) {
    return "";
  }

  if (selectedKey === "E") {
    return (
      getInputValue(financialAnalysis, "insurancePurchaseOtherObjectives", true) ||
      ZUU_OTHER_OBJECTIVE_LABELS[selectedKey]
    );
  }

  return ZUU_OTHER_OBJECTIVE_LABELS[selectedKey] || "";
}

function resolveZuuObjectiveValue(financialAnalysis, objectiveKey) {
  switch (objectiveKey) {
    case "A":
      return resolveZuuCurrencyObjectiveValue(
        financialAnalysis,
        "additionalLifeCoverageAmountOption",
        "additionalLifeCoverageAmountHKD",
        "additionalLifeCoverageAmountUSD",
      );
    case "B":
      return resolveZuuCurrencyObjectiveValue(
        financialAnalysis,
        "additionalCriticalIllnessCoverageAmountOption",
        "additionalCriticalIllnessCoverageAmountHKD",
        "additionalCriticalIllnessCoverageAmountUSD",
      );
    case "C":
    case "D":
    case "E":
      return resolveZuuTargetAmountValue(financialAnalysis);
    case "F":
      return resolveZuuOtherObjectiveValue(financialAnalysis);
    default:
      return "";
  }
}

function resolvePaymentPeriod(financialAnalysis, isZuuMode) {
  const key = getSelectedKey(
    financialAnalysis,
    "willingPremiumPaymentPeriodRange",
    isZuuMode,
  );

  if (!key) {
    return PENDING_TEXT;
  }

  const baseText = PAYMENT_PERIOD_MAPPING[key];
  if (!baseText) {
    return PENDING_TEXT;
  }

  if (key === "G") {
    const amount = getInputValue(
      financialAnalysis,
      "singlePremiumPaymentLimit",
      isZuuMode,
    );
    return amount ? `${baseText} ${amount}HKD` : baseText;
  }

  return baseText;
}

function resolveCoveragePeriod(financialAnalysis, isZuuMode) {
  const key = getSelectedKey(
    financialAnalysis,
    "policyTargetExpectedTime",
    isZuuMode,
  );

  return key ? COVERAGE_PERIOD_MAPPING[key] || PENDING_TEXT : PENDING_TEXT;
}

export function resolveFnaNeedsAnalysisDisplay(financialAnalysis, options) {
  const isZuuMode = Boolean(options?.isZuuMode);
  const selectedObjectives = getSelectedKeys(
    financialAnalysis,
    "insurancePurchaseObjectives",
    isZuuMode,
  );

  const targetTags = selectedObjectives.reduce((tags, objectiveKey) => {
    const value = isZuuMode
      ? resolveZuuObjectiveValue(financialAnalysis, objectiveKey)
      : resolveLegacyObjectiveValue(financialAnalysis, objectiveKey);

    if (!value) {
      return tags;
    }

    tags.push({
      label: objectiveKey,
      value,
    });
    return tags;
  }, []);

  return {
    targetTags,
    paymentPeriod: resolvePaymentPeriod(financialAnalysis, isZuuMode),
    coveragePeriod: resolveCoveragePeriod(financialAnalysis, isZuuMode),
  };
}
